import { useEffect } from "react";

import { Episode } from "@/types/Episode";
import { events } from "@/utils";
import Modal, { ModalContent, ModalHeader } from "@components/Modal";
import { ModalProps } from "@components/Modal/Modal";

import { useEpisode } from "../../../context";

import styles from "./TikTokModal.module.css";

const EMBED_SCRIPT_ID = "tiktok-embed";

/**
 * TikTokModal
 * The reason for all of the logic in the useEffect below is that the TikTok embed script needs to load _after_ the bloquote
 * is rendered. Hence we need to replace it if it exists, and add it if it doesn't. _After_ we've rendered everything.
 */
const TiktokModal: React.FunctionComponent<
  {
    episode: Episode;
  } & ModalProps
> = ({ show, ...props }) => {
  const episode = useEpisode();

  // Stop any current playback when opening the modal.
  useEffect(() => {
    show && document.dispatchEvent(events.stopAllPlayingAudio);
  }, [show]);

  useEffect(() => {
    if (show) {
      let mounted = true;
      const head = document.getElementsByTagName("head")[0];
      const script = document.createElement("script");
      script.id = EMBED_SCRIPT_ID;
      script.src = "https://www.tiktok.com/embed.js";

      window.requestAnimationFrame(() => {
        if (mounted) {
          const current = document.getElementById(EMBED_SCRIPT_ID);
          if (current) {
            head.replaceChild(script, current);
          } else {
            head.appendChild(script);
          }
        }
      });
      return () => {
        mounted = false;
      };
    }
  }, [show]);

  if (!episode.episode_guid) {
    return null;
  }

  return (
    <Modal
      className={styles.root}
      show={show}
      {...props}
      data-testid="tiktok-embed-modal"
    >
      <ModalHeader>{episode.title}</ModalHeader>
      <ModalContent>
        <blockquote
          className="tiktok-embed"
          data-testid="tiktok-embed"
          cite={episode.episode_public_url}
          data-video-id={episode.episode_guid.replace("tiktok_", "")}
          style={{
            maxWidth: 325,
            minWidth: 225,
            minHeight: 640,
            margin: 0,
          }}
        >
          <section />
        </blockquote>
      </ModalContent>
    </Modal>
  );
};

export default TiktokModal;
